import { DateTime } from "luxon";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid } from "../Grid";
import { SelectCard } from "../SelectCard";
import { useCalendar } from "./CalendarContext";
import styles from "./Calendar.module.scss";
import cx from "classnames";
import { useMergeRefs } from "../../hooks";

export type CalendarMonthSelectionProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * The function to call when a month is selected.
   *
   * @param month - The month that is selected.
   */
  onMonthSelection: (month: DateTime["month"]) => void;
};

/**
 * A component that displays a grid of month cards and allows the user to select a month.
 *
 * @param props.onMonthSelection - The function to call when a month is selected.
 * @param props - The rest of the props for the div wrapper.
 * @returns A component that displays a grid of month cards and allows the user to select a month.
 */
export const CalendarMonthSelection = forwardRef<
  HTMLDivElement,
  CalendarMonthSelectionProps
>(({ onMonthSelection, ...props }, ref) => {
  const { defaultTimeZone, maxDate, minDate, selectedMonth, selectedYear } =
    useCalendar();

  const monthSelectionRef = useRef<HTMLDivElement>(null);
  const combinedRef = useMergeRefs([ref, monthSelectionRef]);

  const currentDate = useMemo(
    () =>
      DateTime.fromObject({ year: selectedYear, month: selectedMonth })
        .setZone(defaultTimeZone)
        .startOf("day"),
    [defaultTimeZone, selectedYear, selectedMonth],
  );

  const dates = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const date = DateTime.fromObject({
          year: selectedYear,
          month: 1,
        }).plus({
          month: i,
        });

        // disable the select card if outside the min or max date
        const disabled =
          (minDate && date < minDate && !date.hasSame(minDate, "month")) ||
          (maxDate && date > maxDate && !date.hasSame(maxDate, "month"));

        return {
          month: date.month,
          disabled: disabled ?? false,
        };
      }),
    [maxDate, minDate, selectedYear],
  );

  const classes = cx(styles["month-selection"], props.className);

  const [focusedIndex, setFocusedIndex] = useState(selectedMonth - 1);

  // reset focused index when current date changes
  useEffect(() => {
    if (minDate || maxDate) {
      // find the next enabled month
      let index = dates.findIndex(({ month }) => month === selectedMonth);
      while (dates[index].disabled && index < 11) {
        index++;
      }
      setFocusedIndex(index);
    } else {
      setFocusedIndex(selectedMonth - 1);
    }
  }, [selectedMonth, minDate, maxDate, dates]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const cards = monthSelectionRef.current?.querySelectorAll(
        "input[type='checkbox']",
      );
      if (!cards || cards.length === 0) {
        return;
      }

      const cardArray = Array.from(cards);
      const currentIndex = cardArray.indexOf(target as HTMLButtonElement);

      if (currentIndex === -1) return;

      const updateFocusedIndex = (index: number) => {
        e.preventDefault();
        (cardArray[index] as HTMLButtonElement).focus();
        setFocusedIndex(index);
      };

      let newIndex: number;
      switch (e.key) {
        case "ArrowLeft":
          newIndex = currentIndex > 0 ? currentIndex - 1 : 11;
          while (dates[newIndex].disabled) {
            newIndex = newIndex > 0 ? newIndex - 1 : 11;
          }
          updateFocusedIndex(newIndex);
          break;

        case "ArrowRight":
          newIndex = currentIndex < 11 ? currentIndex + 1 : 0;
          while (dates[newIndex].disabled) {
            newIndex = newIndex < 11 ? newIndex + 1 : 0;
          }
          updateFocusedIndex(newIndex);
          break;

        case "ArrowUp":
          if (currentIndex < 3) {
            newIndex = currentIndex + 9;
          } else {
            newIndex = currentIndex - 3;
          }
          while (dates[newIndex].disabled) {
            if (newIndex > 2) {
              newIndex -= 3;
            } else {
              newIndex += 9;
            }
          }
          updateFocusedIndex(newIndex);
          break;

        case "ArrowDown":
          if (currentIndex > 8) {
            newIndex = currentIndex - 9;
          } else {
            newIndex = currentIndex + 3;
          }
          while (dates[newIndex].disabled) {
            if (newIndex < 9) {
              newIndex += 3;
            } else {
              newIndex -= 9;
            }
          }
          updateFocusedIndex(newIndex);
          break;

        case "Enter":
        case " ":
          e.preventDefault();
          if (!dates[currentIndex].disabled) {
            onMonthSelection(dates[currentIndex].month);
          }
          break;
      }
    },
    [dates, onMonthSelection],
  );

  return (
    <div
      ref={combinedRef}
      data-anv="calendar-month-selection"
      className={classes}
      {...props}
    >
      <div role="grid" aria-label="Calendar month selection">
        <Grid
          templateColumns="repeat(3, minmax(0, 1fr))"
          gap={3}
          onKeyDown={handleKeyDown}
          role="row"
        >
          {dates.map(({ month, disabled }, index) => (
            <div role="gridcell" key={month.toString()}>
              <SelectCard
                id={month.toString()}
                disabled={disabled}
                onChange={() => onMonthSelection(month)}
                checked={
                  selectedMonth === month && selectedYear === currentDate.year
                }
                checkboxProps={{
                  className: styles["selection-checkbox"],
                  tabIndex: focusedIndex === index ? 0 : -1,
                }}
                className={styles["selection-card"]}
              >
                {DateTime.fromObject({ month }).toFormat("MMMM")}
              </SelectCard>
            </div>
          ))}
        </Grid>
      </div>
    </div>
  );
});

CalendarMonthSelection.displayName = "CalendarMonthSelection";
